import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../api';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  Check, Loader2, Calendar, Sparkles, AlertCircle, CheckCircle, Crown, Zap, Building, Receipt, ArrowDownRight, ArrowUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const fmtMoney = (v) => {
  const n = Number(v ?? 0);
  return `€${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

export const StudioSubscriptionPage = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  // Confirm flow state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('select'); // select | success

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes, ledgerRes] = await Promise.all([
        apiGet('/api/subscription-plans'),
        apiGet('/api/studio/subscription').catch(() => ({ subscription: null })),
        apiGet('/api/studio/subscription/ledger').catch(() => ({ entries: [] })),
      ]);
      setPlans(plansRes);
      setCurrentSubscription(subRes.subscription);
      setLedger(ledgerRes.entries || []);
    } catch {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (plan) => {
    setSelectedPlan(plan);
    setBillingCycle(currentSubscription?.billing_cycle || 'monthly');
    setStep('select');
    setShowConfirmDialog(true);
  };

  const submitPlan = async () => {
    if (!selectedPlan) return;
    setSubmitting(true);
    try {
      const data = await apiPost('/api/studio/subscription', {
        body: { plan_id: selectedPlan.id, billing_cycle: billingCycle },
      });
      setCurrentSubscription(data.subscription);
      setStep('success');
      toast.success(data.message || 'Plan updated');
      // Refresh ledger so the new charge/credit show up immediately.
      const ledgerRes = await apiGet('/api/studio/subscription/ledger').catch(() => ({ entries: [] }));
      setLedger(ledgerRes.entries || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not update plan'));
    } finally {
      setSubmitting(false);
    }
  };

  const closeDialog = () => {
    setShowConfirmDialog(false);
    if (step === 'success') fetchData();
  };

  const planIcon = (i) => [Building, Zap, Crown][i % 3];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const ledgerNet = ledger.reduce(
    (acc, e) => acc + (e.type === 'charge' ? Number(e.amount_eur) : -Number(e.amount_eur)),
    0,
  );
  const outstandingNet = ledger.reduce(
    (acc, e) =>
      e.invoiced_at
        ? acc
        : acc + (e.type === 'charge' ? Number(e.amount_eur) : -Number(e.amount_eur)),
    0,
  );

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="studio-subscription-page">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Choose Your Plan</h1>
          <p className="text-muted-foreground">
            Upgrade or downgrade anytime. Plan changes are applied immediately and any pro-rated credit/charge is added to your billing ledger below.
          </p>
        </div>

        {/* Current plan summary */}
        {currentSubscription && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="py-5 grid sm:grid-cols-4 gap-4 text-sm">
              <Stat label="Current plan" value={currentSubscription.plan_name || '—'} bold />
              <Stat label="Cycle" value={currentSubscription.billing_cycle || '—'} />
              <Stat label="Renews" value={fmtDate(currentSubscription.current_period_end)} />
              <Stat label="Anniversary" value={fmtDate(currentSubscription.anniversary_date)} />
            </CardContent>
          </Card>
        )}

        {/* Plans grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, i) => {
            const Icon = planIcon(i);
            const isCurrent =
              currentSubscription?.plan_id === plan.id;
            return (
              <Card key={plan.id} className={isCurrent ? 'border-primary border-2' : ''}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Icon className="w-7 h-7 text-primary" />
                    {isCurrent && <Badge>Current</Badge>}
                  </div>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-3xl font-bold">{fmtMoney(plan.price_monthly)}<span className="text-base text-muted-foreground font-normal"> /mo</span></div>
                    {plan.price_yearly > 0 && (
                      <div className="text-sm text-muted-foreground">or {fmtMoney(plan.price_yearly)} / yr</div>
                    )}
                  </div>
                  {/* Bug R22 fix (Iter-67): show quantitative limits + all
                      features on every plan card so Small Studio (and any
                      plan with a short `features` array) clearly states
                      what's included. Previously we sliced to 5 and never
                      rendered the limits — Small Studio looked empty. */}
                  {(plan.max_classes || plan.max_instructors || plan.max_clients) ? (
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-b py-3 text-center">
                      <div>
                        <div className="text-xs text-muted-foreground">Classes</div>
                        <div className="font-semibold text-sm">
                          {plan.max_classes === -1 ? 'Unlimited' : `Up to ${plan.max_classes}`}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Instructors</div>
                        <div className="font-semibold text-sm">
                          {plan.max_instructors === -1 ? 'Unlimited' : `Up to ${plan.max_instructors}`}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Clients</div>
                        <div className="font-semibold text-sm">
                          {plan.max_clients === -1 ? 'Unlimited' : `Up to ${plan.max_clients}`}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <ul className="space-y-2 text-sm" data-testid={`plan-${i}-features`}>
                    {(plan.features && plan.features.length > 0
                      ? plan.features
                      : ['Core scheduling', 'Client management', 'Email notifications']
                    ).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={i === 1 ? 'default' : 'outline'}
                    onClick={() => openConfirm(plan)}
                    disabled={isCurrent}
                    data-testid={`select-plan-btn-${i}`}
                  >
                    {isCurrent ? 'Current Plan' : currentSubscription ? 'Switch to this plan' : 'Select plan'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {plans.length === 0 && (
          <Card className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground text-lg">No subscription plans available</p>
          </Card>
        )}

        {/* Billing ledger */}
        <Card data-testid="subscription-ledger-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Billing Ledger
              </CardTitle>
              <CardDescription>
                Pro-rated credits and charges your studio will be invoiced for off-platform.
              </CardDescription>
            </div>
            {ledger.length > 0 && (
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground uppercase">Outstanding</div>
                  <div
                    className={`text-xl font-semibold ${
                      outstandingNet >= 0 ? 'text-amber-700' : 'text-emerald-600'
                    }`}
                    data-testid="ledger-outstanding"
                  >
                    {outstandingNet >= 0 ? fmtMoney(outstandingNet) : `−${fmtMoney(Math.abs(outstandingNet))}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground uppercase">Net</div>
                  <div className={`text-xl font-semibold ${ledgerNet >= 0 ? 'text-foreground' : 'text-emerald-600'}`}>
                    {ledgerNet >= 0 ? fmtMoney(ledgerNet) : `-${fmtMoney(Math.abs(ledgerNet))}`}
                  </div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {ledger.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">
                No charges or credits yet. Your ledger updates whenever you change plans.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground border-b">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Period</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((e) => {
                      const isInvoiced = !!e.invoiced_at;
                      return (
                        <tr key={e.id} className="border-b last:border-b-0" data-testid={`ledger-row-${e.id}`}>
                          <td className="px-4 py-3 text-muted-foreground">{fmtDate(e.created_at)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {e.type === 'credit' ? (
                                <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <ArrowUpRight className="w-4 h-4 text-amber-600" />
                              )}
                              <span className={isInvoiced ? 'line-through text-muted-foreground' : ''}>
                                {e.description}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {fmtDate(e.period_from)} – {fmtDate(e.period_to)}
                          </td>
                          <td className="px-4 py-3">
                            {isInvoiced ? (
                              <div className="space-y-0.5" data-testid={`status-invoiced-${e.id}`}>
                                <Badge className="bg-emerald-500 text-white text-xs">Invoiced</Badge>
                                {e.invoice_ref && (
                                  <div className="text-xs font-mono text-muted-foreground">{e.invoice_ref}</div>
                                )}
                                <div className="text-xs text-muted-foreground">
                                  {fmtDate(e.invoiced_at)}
                                </div>
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs" data-testid={`status-outstanding-${e.id}`}>
                                Outstanding
                              </Badge>
                            )}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${e.type === 'credit' ? 'text-emerald-600' : ''} ${isInvoiced ? 'opacity-60' : ''}`}>
                            {e.type === 'credit' ? '−' : ''}{fmtMoney(e.amount_eur)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={closeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {step === 'success'
                ? 'Plan updated!'
                : currentSubscription
                ? `Switch to ${selectedPlan?.name}`
                : `Subscribe to ${selectedPlan?.name}`}
            </DialogTitle>
            <DialogDescription>
              {step === 'select' && 'Pick your billing cycle. We\'ll pro-rate against your anniversary.'}
              {step === 'success' && 'Your plan is now active.'}
            </DialogDescription>
          </DialogHeader>

          {step === 'select' && selectedPlan && (
            <div className="space-y-6 py-4">
              <RadioGroup value={billingCycle} onValueChange={setBillingCycle}>
                <div className="space-y-3">
                  <CycleOption
                    value="monthly"
                    selected={billingCycle === 'monthly'}
                    label="Monthly"
                    sub="Billed monthly, anchored to your anniversary"
                    price={`${fmtMoney(selectedPlan.price_monthly)}/mo`}
                  />
                  {selectedPlan.price_yearly > 0 && (
                    <CycleOption
                      value="yearly"
                      selected={billingCycle === 'yearly'}
                      label="Annual"
                      sub={`Save ${Math.round((1 - selectedPlan.price_yearly / (selectedPlan.price_monthly * 12)) * 100)}% — billed once a year`}
                      price={`${fmtMoney(selectedPlan.price_yearly)}/yr`}
                    />
                  )}
                </div>
              </RadioGroup>

              <div className="p-4 bg-muted rounded-lg text-sm space-y-1.5">
                <p className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  <span>
                    Pro-rated charge applied to the days remaining until your next anniversary.
                    Any unused days on your current plan become a pro-rated credit on your ledger.
                  </span>
                </p>
                <p className="text-xs text-muted-foreground pt-1">
                  No card required — billing happens off-platform.
                </p>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Welcome to {selectedPlan?.name}!</h3>
              <p className="text-muted-foreground text-sm">
                Pro-rated entries have been added to your billing ledger.
              </p>
            </div>
          )}

          <DialogFooter>
            {step === 'select' && (
              <>
                <Button variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button onClick={submitPlan} disabled={submitting} data-testid="confirm-plan-btn">
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {currentSubscription ? 'Switch plan' : 'Activate plan'}
                </Button>
              </>
            )}
            {step === 'success' && (
              <Button onClick={closeDialog} className="w-full">
                <Sparkles className="w-4 h-4 mr-2" />
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

const Stat = ({ label, value, bold }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-0.5 ${bold ? 'font-semibold text-base' : ''}`}>{value}</div>
  </div>
);

const CycleOption = ({ value, selected, label, sub, price }) => (
  <Label
    htmlFor={value}
    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
      selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
    }`}
  >
    <div className="flex items-center gap-3">
      <RadioGroupItem value={value} id={value} />
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
    </div>
    <span className="font-bold text-sm">{price}</span>
  </Label>
);

export default StudioSubscriptionPage;
